import { useEffect, useMemo, useState } from "react";
import {
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type OnChangeFn,
  type PaginationState,
  type SortingState,
  useReactTable,
} from "@tanstack/react-table";
import { type TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { TriangleAlert } from "lucide-react";

import Loading from "@/components/Loading";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { getColumnSortAria, SortableColumnHeader } from "@/components/ui/sortable-column-header";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { DataTablePagination } from "@/components/ui/table-pagination";
import { cn } from "@/lib/utils";

import { ActionsCell } from "./components/ActionsCell";
import { ActiveBadge } from "./components/ActiveBadge";
import { AuditCell } from "./components/AuditCell";
import { COMPACT_HEADER_CLASS, DEFAULT_PAGE_SIZE, EMPTY_VALUE } from "./constants";
import { type FarmerIdentificationColumnDef } from "./types";
import {
  filterFarmerIdentification,
  getCode,
  getColumnMeta,
  getLengthRange,
  getName,
  getRowId,
  getTimestamp,
} from "./utils";
import { toValueFormat, VALUE_FORMAT_LABEL_KEYS } from "../../constants";
import {
  type FarmerIdentificationType,
  type SetFarmerIdentificationModalOptions,
} from "../../types";

interface ListProps {
  errorMessage?: string;
  farmerIdentification?: FarmerIdentificationType[];
  isFetching?: boolean;
  onSortingChange: OnChangeFn<SortingState>;
  pageSize?: number;
  searchTerm?: string;
  setModalOptions: SetFarmerIdentificationModalOptions;
  sorting: SortingState;
}

const getValueFormatLabel = (identificationType: FarmerIdentificationType, t: TFunction) => {
  const valueFormat = toValueFormat(identificationType.value_format);

  return valueFormat ? t(VALUE_FORMAT_LABEL_KEYS[valueFormat]) : EMPTY_VALUE;
};

const createColumns = ({
  onOpenModal,
  t,
}: {
  onOpenModal: (identificationType: FarmerIdentificationType, type: "edit" | "view") => void;
  t: TFunction;
}): FarmerIdentificationColumnDef[] => [
  {
    id: "actions",
    header: () => (
      <span className="sr-only">
        {t("common.view")} / {t("common.edit")}
      </span>
    ),
    cell: ({ row }) => (
      <ActionsCell identificationType={row.original} onOpenModal={onOpenModal} t={t} />
    ),
    enableSorting: false,
    meta: {
      cellClassName: "w-0 px-3 align-top",
      headerClassName: "w-0",
    },
  },
  {
    id: "name",
    accessorFn: getName,
    header: ({ column }) => (
      <SortableColumnHeader column={column} label={t("configuration.common.name")} />
    ),
    cell: ({ row }) =>
      getName(row.original) || <span className="text-neutral-alpha-300">{EMPTY_VALUE}</span>,
    sortDescFirst: false,
    sortingFn: "alphanumeric",
    sortUndefined: "last",
    meta: {
      cellClassName:
        "min-w-48 max-w-72 whitespace-normal px-3 align-top font-medium text-foreground",
      headerClassName: cn("min-w-48 max-w-72", COMPACT_HEADER_CLASS),
    },
  },
  {
    id: "code",
    accessorFn: getCode,
    header: ({ column }) => (
      <SortableColumnHeader column={column} label={t("configuration.common.code")} />
    ),
    cell: ({ row }) =>
      getCode(row.original) || <span className="text-neutral-alpha-300">{EMPTY_VALUE}</span>,
    sortDescFirst: false,
    sortingFn: "alphanumeric",
    sortUndefined: "last",
    meta: {
      cellClassName: "min-w-28 max-w-40 px-3 align-top",
      headerClassName: cn("min-w-28 max-w-40", COMPACT_HEADER_CLASS),
    },
  },
  {
    id: "format",
    accessorFn: row => getValueFormatLabel(row, t),
    header: ({ column }) => (
      <SortableColumnHeader
        column={column}
        label={t("farmerIdentification.form.identificationFormat")}
      />
    ),
    cell: ({ row }) => getValueFormatLabel(row.original, t),
    sortDescFirst: false,
    sortingFn: "alphanumeric",
    sortUndefined: "last",
    meta: {
      cellClassName: "min-w-32 max-w-44 px-3 align-top",
      headerClassName: cn("min-w-32 max-w-44", COMPACT_HEADER_CLASS),
    },
  },
  {
    id: "length",
    accessorFn: row => row.value_minimum_length ?? undefined,
    header: ({ column }) => (
      <SortableColumnHeader
        align="end"
        column={column}
        label={t("farmerIdentification.list.column.length")}
      />
    ),
    cell: ({ row }) => getLengthRange(row.original),
    sortingFn: "basic",
    sortUndefined: "last",
    meta: {
      cellClassName: "max-w-32 px-3 text-right align-top",
      headerClassName: cn("max-w-32 text-right", COMPACT_HEADER_CLASS),
    },
  },
  {
    id: "lastUpdated",
    accessorFn: row => getTimestamp(row.last_update_datetime),
    header: ({ column }) => (
      <SortableColumnHeader column={column} label={t("configuration.list.column.lastUpdated")} />
    ),
    cell: ({ row }) => (
      <AuditCell
        dateTime={row.original.last_update_datetime}
        t={t}
        user={row.original.last_update_user}
      />
    ),
    sortingFn: "basic",
    sortUndefined: "last",
    meta: {
      cellClassName: "min-w-40 px-3 align-top",
      headerClassName: cn("min-w-40", COMPACT_HEADER_CLASS),
    },
  },
  {
    id: "created",
    accessorFn: row => getTimestamp(row.creation_datetime),
    header: ({ column }) => (
      <SortableColumnHeader column={column} label={t("configuration.list.column.created")} />
    ),
    cell: ({ row }) => (
      <AuditCell
        dateTime={row.original.creation_datetime}
        t={t}
        user={row.original.creation_user}
      />
    ),
    sortingFn: "basic",
    sortUndefined: "last",
    meta: {
      cellClassName: "min-w-40 px-3 align-top",
      headerClassName: cn("min-w-40", COMPACT_HEADER_CLASS),
    },
  },
  {
    id: "active",
    accessorFn: row => Boolean(row.active),
    header: ({ column }) => (
      <SortableColumnHeader column={column} label={t("configuration.common.active")} />
    ),
    cell: ({ row }) => <ActiveBadge isActive={row.original.active} t={t} />,
    sortingFn: "basic",
    sortUndefined: "last",
    meta: {
      cellClassName: "min-w-24 px-3 align-top",
      headerClassName: cn("min-w-24", COMPACT_HEADER_CLASS),
    },
  },
];

export const List = ({
  errorMessage = "",
  farmerIdentification = [],
  isFetching = false,
  onSortingChange,
  pageSize = DEFAULT_PAGE_SIZE,
  searchTerm = "",
  setModalOptions,
  sorting,
}: ListProps) => {
  const { t } = useTranslation();
  const resolvedPageSize = pageSize > 0 ? pageSize : DEFAULT_PAGE_SIZE;
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: resolvedPageSize,
  });
  const columns = useMemo(
    () =>
      createColumns({
        onOpenModal: (identificationType, type) =>
          setModalOptions({ id: identificationType.id ?? undefined, isOpen: true, type }),
        t,
      }),
    [setModalOptions, t]
  );
  const filteredFarmerIdentification = useMemo(
    () => filterFarmerIdentification(farmerIdentification, searchTerm),
    [farmerIdentification, searchTerm]
  );

  useEffect(() => {
    setPagination(currentPagination =>
      currentPagination.pageSize === resolvedPageSize
        ? currentPagination
        : { pageIndex: 0, pageSize: resolvedPageSize }
    );
  }, [resolvedPageSize]);

  useEffect(() => {
    setPagination(currentPagination =>
      currentPagination.pageIndex === 0 ? currentPagination : { ...currentPagination, pageIndex: 0 }
    );
  }, [searchTerm, sorting]);

  useEffect(() => {
    setPagination(currentPagination => {
      const pageCount = Math.max(
        1,
        Math.ceil(filteredFarmerIdentification.length / currentPagination.pageSize)
      );
      const nextPageIndex = Math.min(currentPagination.pageIndex, pageCount - 1);

      return nextPageIndex === currentPagination.pageIndex
        ? currentPagination
        : { ...currentPagination, pageIndex: nextPageIndex };
    });
  }, [filteredFarmerIdentification.length]);

  const table = useReactTable({
    autoResetPageIndex: false,
    columns,
    data: filteredFarmerIdentification,
    enableMultiSort: false,
    enableSortingRemoval: false,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getRowId,
    getSortedRowModel: getSortedRowModel(),
    onPaginationChange: setPagination,
    onSortingChange,
    state: {
      pagination,
      sorting,
    },
  });

  if (errorMessage) {
    return (
      <Alert variant="destructive">
        <TriangleAlert aria-hidden="true" />
        <AlertDescription>{errorMessage}</AlertDescription>
      </Alert>
    );
  }

  const rows = table.getRowModel().rows;
  const columnCount = table.getVisibleLeafColumns().length;

  return (
    <div className="overflow-hidden rounded-md border border-border bg-background">
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map(headerGroup => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map(header => (
                <TableHead
                  key={header.id}
                  className={getColumnMeta(header.column.columnDef)?.headerClassName}
                  aria-sort={getColumnSortAria(header.column)}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(header.column.columnDef.header, header.getContext())}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {isFetching ? (
            <TableRow>
              <TableCell colSpan={columnCount}>
                <Loading isVisible small />
              </TableCell>
            </TableRow>
          ) : null}
          {!isFetching && rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={columnCount} className="h-24 text-center text-muted-foreground">
                {t("farmerIdentification.empty")}
              </TableCell>
            </TableRow>
          ) : null}
          {!isFetching
            ? rows.map(row => (
                <TableRow key={row.id}>
                  {row.getVisibleCells().map(cell => (
                    <TableCell
                      key={cell.id}
                      className={getColumnMeta(cell.column.columnDef)?.cellClassName}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            : null}
        </TableBody>
      </Table>
      {rows.length > 0 ? <DataTablePagination table={table} /> : null}
    </div>
  );
};
